import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MenuItem, Stack, Typography } from '@mui/material';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  ADMIN_DURATION_CUSTOM,
  ADMIN_DURATION_OPTIONS,
  ADMIN_DURATION_PERMANENT,
  USER_ROLE_MEMBER,
} from 'js/constants/userRoles';
import DateTimeField from 'js/components/elements/DateTimeField';
import { getLocalTimeZoneLabel } from 'js/lib/DateUtils';
import { Select } from 'new/elements/Select';

interface Props {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  roleFieldName: string;
  expirationFieldName: string;
  initialExpiresAt?: string | null;
  label?: string;
}

function getPresetMs(presetValue: string): number {
  return Date.now() + Number(presetValue) * 1000;
}

// Renders the DatePicker popup into document.body so it floats above the modal
// dialog instead of being clipped by DialogContent's overflow container.
function PortalDateTimeField({
  value,
  onChange,
  datePickerProps,
  customStyles,
}: {
  value: number | null;
  onChange: (ms: number | null) => void;
  datePickerProps?: Record<string, any>;
  customStyles?: Record<string, any>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable component created once per instance; closes over containerRef safely
  // because ref objects are stable across renders.
  const PopupComponent = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, { open: boolean; children: React.ReactNode }>(
        ({ open, children }, forwardedRef) => {
          const popupRef = useRef<HTMLDivElement>(null);
          const [style, setStyle] = useState<React.CSSProperties>({
            position: 'fixed',
            visibility: 'hidden',
            zIndex: 9999,
          });

          useLayoutEffect(() => {
            if (!open || !containerRef.current || !popupRef.current) return;
            const inputRect = containerRef.current.getBoundingClientRect();
            const popupHeight = popupRef.current.offsetHeight;
            const fitsBelow = inputRect.bottom + 2 + popupHeight <= window.innerHeight;
            setStyle({
              position: 'fixed',
              top: fitsBelow ? inputRect.bottom + 2 : inputRect.top - popupHeight - 2,
              left: inputRect.left,
              width: inputRect.width,
              zIndex: 9999,
              visibility: 'visible',
            });
          }, [open]);

          if (!open) return null;

          return createPortal(
            <div
              ref={(el) => {
                (popupRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                if (typeof forwardedRef === 'function') forwardedRef(el);
                else if (forwardedRef) forwardedRef.current = el;
              }}
              className="rw-popup"
              style={style}
            >
              {children}
            </div>,
            document.body,
          );
        },
      ),
    [],
  );

  return (
    <div ref={containerRef}>
      <DateTimeField
        value={value}
        onChange={onChange}
        datePickerProps={{ ...datePickerProps, popupComponent: PopupComponent }}
        customStyles={customStyles}
      />
    </div>
  );
}

export function AdminDurationSelect({
  control,
  setValue,
  roleFieldName,
  expirationFieldName,
  initialExpiresAt,
  label = 'Select duration',
}: Props) {
  const role = useWatch({ control, name: roleFieldName });
  const isTimedRole = !!role && role !== USER_ROLE_MEMBER;

  const [preset, setPreset] = useState<string>(() =>
    initialExpiresAt ? ADMIN_DURATION_CUSTOM : ADMIN_DURATION_PERMANENT,
  );
  const [pickerMs, setPickerMs] = useState<number | null>(() =>
    initialExpiresAt ? new Date(initialExpiresAt).getTime() : null,
  );

  useEffect(() => {
    if (!isTimedRole) {
      setValue(expirationFieldName, null);
      return;
    }
    if (preset === ADMIN_DURATION_PERMANENT || pickerMs === null) {
      setValue(expirationFieldName, null);
    } else {
      setValue(expirationFieldName, Math.round((pickerMs - Date.now()) / 1000));
    }
  }, [isTimedRole, preset, pickerMs, expirationFieldName, setValue]);

  if (!isTimedRole) return null;

  const handlePresetChange = (value: string) => {
    setPreset(value);
    if (value === ADMIN_DURATION_PERMANENT) {
      setPickerMs(null);
    } else if (value !== ADMIN_DURATION_CUSTOM) {
      setPickerMs(getPresetMs(value));
    } else if (pickerMs === null) {
      setPickerMs(getPresetMs(String(7 * 86400)));
    }
  };

  return (
    <Stack direction="column" gap={2}>
      <Select
        label={label}
        fullWidth
        value={preset}
        onChange={(e) => handlePresetChange(e.target.value as string)}
        items={ADMIN_DURATION_OPTIONS.map((o) => ({ value: o.value, title: o.label }))}
        renderItem={(item) => (
          <MenuItem key={item.value} value={item.value}>
            <Typography>{item.title}</Typography>
          </MenuItem>
        )}
        renderValue={(value) => (
          <Typography>{ADMIN_DURATION_OPTIONS.find((o) => o.value === value)?.label}</Typography>
        )}
      />
      {preset !== ADMIN_DURATION_PERMANENT && (
        <Stack direction="row" alignItems="center" gap={1}>
          <PortalDateTimeField
            value={pickerMs}
            onChange={(ms: number | null) => {
              setPickerMs(ms);
              setPreset(ADMIN_DURATION_CUSTOM);
            }}
            datePickerProps={{
              min: new Date(),
              inputProps: { style: { pointerEvents: 'none', cursor: 'default' }, tabIndex: -1 },
            }}
            customStyles={{
              container: { minWidth: 270, fontSize: 14, marginTop: 0, marginBottom: 0 },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {getLocalTimeZoneLabel()}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
